/**
 * The animated star sky behind QuFolio's sign-in screen — a port of the hero on
 * qufolio.com (`ts/apps/web/sites/qufolio/index.html`, "Sky: nebula + star
 * cluster"), so the page you come from and the page you sign in on are one sky.
 *
 * Ported as it is, numbers and all: a nebula painted once per size onto an
 * offscreen canvas, then stars, twinkle, parallax and the odd shooting star
 * drawn each frame. What differs is only what a component needs — `startSky`
 * returns a stop function that cancels the frame loop and drops its listeners,
 * and nothing reads the landing page's hero rotation.
 *
 * Pure canvas, no React; `components/SignInBackdrop.tsx` owns the element.
 */

/** One sky: the landing page's `HEROES[n].sky`, without the copy around it. */
export interface Sky {
  readonly name: string;
  readonly style?: "veil" | "orion" | "pleiades" | "ring" | "globular" | "galaxy";
  readonly hues?: readonly (readonly [number, number, number])[];
  readonly gain?: number;
  readonly cluster?: number;
  readonly spread?: number;
  readonly round?: boolean;
  readonly brightRate?: number;
  readonly palette?: readonly string[];
  readonly cx: number;
  readonly cy: number;
}

/** The ten skies, in the landing page's order. */
export const SKIES: readonly Sky[] = [
  { name: "Emission nebula", hues: [[90, 70, 200], [197, 90, 220], [60, 120, 230], [255, 110, 150], [80, 200, 230]], cx: 0.68, cy: 0.45 },
  { name: "Dust lit by starlight", gain: 0.5, hues: [[230, 150, 60], [255, 200, 110], [200, 90, 70], [255, 130, 90], [150, 110, 220]], cx: 0.7, cy: 0.42 },
  { name: "The Veil Nebula", style: "veil", cluster: 0.12, cx: 0.78, cy: 0.48 },
  { name: "Star-forming nebula", hues: [[210, 70, 160], [255, 110, 190], [120, 70, 220], [255, 150, 120], [90, 120, 240]], cx: 0.72, cy: 0.46 },
  { name: "Reflection nebula", hues: [[50, 80, 210], [90, 140, 255], [30, 60, 170], [140, 110, 255], [70, 190, 255]], cx: 0.64, cy: 0.44 },
  { name: "The Orion Nebula", style: "orion", cluster: 0.25, cx: 0.72, cy: 0.46 },
  { name: "The Pleiades", style: "pleiades", cluster: 0.35, spread: 0.2, cx: 0.74, cy: 0.42, palette: ["#cfe0ff", "#b8ceff", "#e6eeff", "#ffffff", "#a9c4ff"] },
  { name: "The Helix Nebula", style: "ring", cluster: 0, cx: 0.75, cy: 0.47 },
  { name: "Globular cluster", style: "globular", cluster: 4.5, spread: 0.12, round: true, brightRate: 0.02, cx: 0.72, cy: 0.46, palette: ["#ffe2b0", "#ffd28a", "#fff1d6", "#ffffff", "#ffc070", "#cfe0ff"] },
  { name: "Spiral galaxy", style: "galaxy", cluster: 0, cx: 0.72, cy: 0.47 },
];

const LAST_KEY = "qf.signin.sky";

/**
 * A different sky from last time in this browser — the landing page's rule.
 * Storage that throws (a private window) just means any sky.
 */
export function pickSky(random: () => number = Math.random): number {
  let last = -1;
  try {
    last = Number(localStorage.getItem(LAST_KEY) ?? -1);
  } catch {
    /* no storage: any sky will do */
  }
  let i = Math.floor(random() * (SKIES.length - 1));
  if (i >= last && last >= 0) i++;
  try {
    localStorage.setItem(LAST_KEY, String(i));
  } catch {
    /* not remembered; the next visit may repeat it */
  }
  return i;
}

interface Star {
  x: number;
  y: number;
  layer: number;
  r: number;
  col: string;
  base: number;
  tw: number;
  ph: number;
  spike: boolean;
  pinned: boolean;
}

interface Shooter {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
}

type Rgb = readonly [number, number, number];

const PALETTE = ["#9bb0ff", "#aabfff", "#cad7ff", "#f8f7ff", "#fff4ea", "#ffd2a1", "#ffcc6f"];
const PLEIADES: readonly (readonly [number, number])[] = [
  [0, 0], [0.55, 0.05], [0.6, -0.08], [-0.2, 0.25], [-0.55, 0.1], [-0.35, -0.25], [-0.6, -0.35], [-0.8, -0.05], [-0.6, -0.55],
];

const rand = (a: number, b: number) => a + Math.random() * (b - a);
const gauss = () => {
  let u = 0;
  let v = 0;
  while (!u) u = Math.random();
  while (!v) v = Math.random();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
};

/** Draws `sky` on `canvas` until the returned function is called. */
export function startSky(canvas: HTMLCanvasElement, sky: Sky): () => void {
  const ctx = canvas.getContext("2d");
  if (!ctx) return () => {};
  const reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
  let W = 0;
  let H = 0;
  let stars: Star[] = [];
  let shooters: Shooter[] = [];
  let nebula: HTMLCanvasElement = document.createElement("canvas");
  let mx = 0;
  let my = 0;
  let tx = 0;
  let ty = 0;
  let raf = 0;
  const t0 = performance.now();

  function surface(S: number) {
    const c = document.createElement("canvas");
    c.width = W * S;
    c.height = H * S;
    return { c, g: c.getContext("2d")!, S };
  }
  function blob(g: CanvasRenderingContext2D, x: number, y: number, r: number, [cr, cg, cb]: Rgb, a: number) {
    const grd = g.createRadialGradient(x, y, 0, x, y, r);
    grd.addColorStop(0, `rgba(${cr},${cg},${cb},${a})`);
    grd.addColorStop(1, `rgba(${cr},${cg},${cb},0)`);
    g.fillStyle = grd;
    g.beginPath();
    g.arc(x, y, r, 0, Math.PI * 2);
    g.fill();
  }

  /* The Veil: a few hundred thin strands along the arc of an expanding shell. */
  function buildVeil() {
    const { c, g } = surface(1);
    g.globalCompositeOperation = "lighter";
    g.lineCap = "round";
    const R = Math.max(W, H);
    const arcs = [
      { cx: W * 1.18, cy: H * 0.72, r: R * 0.52, a0: Math.PI * 0.82, a1: Math.PI * 1.36, n: 150, amp: 1 },
      { cx: W * 1.02, cy: H * 0.05, r: R * 0.22, a0: Math.PI * 0.55, a1: Math.PI * 1.02, n: 60, amp: 0.6 },
    ];
    for (const A of arcs) {
      for (let i = 0; i < 40; i++) {
        const t = rand(A.a0, A.a1);
        const rr = A.r + gauss() * R * 0.015;
        const x = A.cx + Math.cos(t) * rr;
        const y = A.cy + Math.sin(t) * rr;
        const hr = R * rand(0.04, 0.09);
        const teal = Math.random() < 0.55;
        const grd = g.createRadialGradient(x, y, 0, x, y, hr);
        grd.addColorStop(0, teal ? "rgba(40,150,160,0.06)" : "rgba(170,50,70,0.05)");
        grd.addColorStop(1, "rgba(0,0,0,0)");
        g.fillStyle = grd;
        g.beginPath();
        g.arc(x, y, hr, 0, Math.PI * 2);
        g.fill();
      }
      for (let k = 0; k < A.n; k++) {
        const kind = Math.random();
        const teal = kind < 0.52;
        const white = kind > 0.94;
        const col = white
          ? "210,235,255"
          : teal
            ? Math.random() < 0.5 ? "70,225,215" : "90,190,240"
            : Math.random() < 0.5 ? "255,85,95" : "255,130,150";
        const base = A.r + gauss() * R * 0.018 * A.amp + (teal ? 0 : R * 0.006);
        const waves = Array.from({ length: 4 }, (_, j) => [R * rand(0.002, 0.012) * A.amp / (j + 1), rand(3, 9) * (j + 1), rand(0, 6.3)]);
        const span = (A.a1 - A.a0) * rand(0.25, 0.9);
        const s0 = rand(A.a0, A.a1 - span);
        const steps = 90;
        const pts: [number, number][] = [];
        for (let i = 0; i <= steps; i++) {
          const t = s0 + (span * i) / steps;
          let rr = base;
          for (const [am, f, ph] of waves) rr += am! * Math.sin(f! * t + ph!);
          pts.push([A.cx + Math.cos(t) * rr, A.cy + Math.sin(t) * rr]);
        }
        const peak = white ? rand(0.18, 0.35) : rand(0.06, 0.22);
        const wide = rand(4, 12);
        const thin = rand(0.5, 1.6);
        for (let i = 0; i < steps; i += 3) {
          const fade = Math.sin((Math.PI * i) / steps) ** 1.5;
          const seg = pts.slice(i, i + 4);
          g.beginPath();
          g.moveTo(...seg[0]!);
          for (const q of seg.slice(1)) g.lineTo(...q);
          g.strokeStyle = `rgba(${col},${peak * fade * 0.12})`;
          g.lineWidth = wide;
          g.stroke();
          g.strokeStyle = `rgba(${col},${peak * fade})`;
          g.lineWidth = thin;
          g.stroke();
        }
      }
    }
    nebula = c;
  }

  /* Orion: two red wings, a pink body, a teal-white core, dark dust from the left. */
  function buildOrion() {
    const { c, g, S } = surface(0.5);
    const cx = W * sky.cx * S;
    const cy = H * sky.cy * S;
    const R = Math.max(W, H) * S;
    g.globalCompositeOperation = "lighter";
    for (const side of [-1, 1])
      for (let i = 0; i < 70; i++) {
        const t = Math.random();
        blob(
          g,
          cx + side * t * R * 0.32 + gauss() * R * 0.03,
          cy - Math.sin(t * Math.PI * 0.9) * R * 0.16 * (side > 0 ? 1 : 0.7) + t * R * 0.06 + gauss() * R * 0.025,
          R * rand(0.03, 0.08),
          Math.random() < 0.6 ? [220, 60, 90] : [255, 110, 150],
          0.07,
        );
      }
    for (let i = 0; i < 90; i++)
      blob(g, cx + gauss() * R * 0.09, cy + gauss() * R * 0.07, R * rand(0.02, 0.07), Math.random() < 0.5 ? [255, 120, 160] : [200, 70, 110], 0.09);
    for (let i = 0; i < 30; i++) blob(g, cx + gauss() * R * 0.035, cy + gauss() * R * 0.03, R * rand(0.02, 0.05), [110, 210, 230], 0.1);
    blob(g, cx, cy, R * 0.12, [255, 200, 220], 0.12);
    blob(g, cx, cy, R * 0.05, [235, 245, 255], 0.45);
    g.globalCompositeOperation = "destination-out";
    for (let i = 0; i < 14; i++) blob(g, cx - R * (0.05 + i * 0.008), cy + R * (0.02 + gauss() * 0.01), R * rand(0.02, 0.04), [0, 0, 0], 0.35);
    for (let i = 0; i < 10; i++) blob(g, cx + gauss() * R * 0.15, cy + R * 0.08 + gauss() * R * 0.04, R * rand(0.02, 0.05), [0, 0, 0], 0.25);
    nebula = c;
  }

  /* Pleiades: blue haze around each sister, streaked along one diagonal. */
  function buildPleiades() {
    const { c, g } = surface(1);
    const R = Math.min(W, H);
    const cx = W * sky.cx;
    const cy = H * sky.cy;
    g.globalCompositeOperation = "lighter";
    g.lineCap = "round";
    for (let i = 0; i < 50; i++) blob(g, cx + gauss() * R * 0.2, cy + gauss() * R * 0.14, R * rand(0.05, 0.14), [90, 140, 255], 0.05);
    for (const [px, py] of PLEIADES) {
      const x = cx + px * R * 0.2;
      const y = cy + py * R * 0.2;
      blob(g, x, y, R * 0.09, [120, 170, 255], 0.16);
      for (let k = 0; k < 26; k++) {
        const off = gauss() * R * 0.04;
        const len = R * rand(0.05, 0.16);
        const ang = -0.45 + gauss() * 0.05;
        const ox = x - Math.sin(ang) * off;
        const oy = y + Math.cos(ang) * off;
        const dx = Math.cos(ang) * len;
        const dy = Math.sin(ang) * len;
        const grd = g.createLinearGradient(ox - dx, oy - dy, ox + dx, oy + dy);
        grd.addColorStop(0, "rgba(140,180,255,0)");
        grd.addColorStop(0.5, `rgba(150,190,255,${rand(0.04, 0.1)})`);
        grd.addColorStop(1, "rgba(140,180,255,0)");
        g.strokeStyle = grd;
        g.lineWidth = rand(1, 4);
        g.beginPath();
        g.moveTo(ox - dx, oy - dy);
        g.lineTo(ox + dx, oy + dy);
        g.stroke();
      }
    }
    nebula = c;
  }

  /* Helix: teal interior, lumpy red ring, faint halo, cometary knots. */
  function buildRing() {
    const { c, g, S } = surface(0.5);
    const cx = W * sky.cx * S;
    const cy = H * sky.cy * S;
    const r = Math.min(W, H) * S * 0.26;
    const P = (a: number, rr: number): [number, number] => [cx + Math.cos(a) * rr * 1.08, cy + Math.sin(a) * rr * 0.92];
    g.globalCompositeOperation = "lighter";
    g.lineCap = "round";
    for (let i = 0; i < 40; i++) blob(g, cx + gauss() * r * 0.3, cy + gauss() * r * 0.3, r * rand(0.3, 0.6), [70, 170, 210], 0.06);
    for (let i = 0; i < 260; i++) {
      const a = rand(0, Math.PI * 2);
      const [x, y] = P(a, r * (1 + gauss() * 0.12 * (1 + 0.5 * Math.cos(a - 0.8))));
      blob(g, x, y, r * rand(0.08, 0.22), Math.random() < 0.6 ? [230, 80, 70] : [255, 150, 90], 0.08);
    }
    for (let i = 0; i < 60; i++) {
      const [x, y] = P(rand(0, Math.PI * 2), r * rand(1.3, 1.7));
      blob(g, x, y, r * rand(0.15, 0.35), [180, 50, 70], 0.03);
    }
    for (let i = 0; i < 220; i++) {
      const a = rand(0, Math.PI * 2);
      const r0 = r * rand(0.62, 0.85);
      const [x0, y0] = P(a, r0);
      const [x1, y1] = P(a, r0 + r * rand(0.04, 0.1));
      g.strokeStyle = `rgba(255,190,150,${rand(0.08, 0.22)})`;
      g.lineWidth = rand(0.6, 1.4);
      g.beginPath();
      g.moveTo(x0, y0);
      g.lineTo(x1, y1);
      g.stroke();
    }
    g.globalCompositeOperation = "destination-out";
    for (let i = 0; i < 40; i++) {
      const [x, y] = P(rand(0, Math.PI * 2), r * 0.55);
      blob(g, x, y, r * 0.18, [0, 0, 0], 0.12);
    }
    nebula = c;
  }

  /* Globular: almost all stars (see buildStars); here only the golden glow. */
  function buildGlobular() {
    const { c, g, S } = surface(0.5);
    const cx = W * sky.cx * S;
    const cy = H * sky.cy * S;
    const R = Math.min(W, H) * S;
    g.globalCompositeOperation = "lighter";
    blob(g, cx, cy, R * 0.3, [255, 214, 160], 0.14);
    blob(g, cx, cy, R * 0.11, [255, 235, 200], 0.28);
    for (let i = 0; i < 30; i++) blob(g, W * S * 0.2 + gauss() * R * 0.3, H * S * 0.85 + gauss() * R * 0.15, R * rand(0.08, 0.2), [80, 60, 160], 0.04);
    nebula = c;
  }

  /* A spiral galaxy at a tilt: disk, two arms, golden bulge, dust lanes, a companion. */
  function buildGalaxy() {
    const { c, g } = surface(1);
    const cx = W * sky.cx;
    const cy = H * sky.cy;
    const R = Math.min(W, H) * 0.42;
    const tilt = -0.55;
    const sq = 0.32;
    const P = (x: number, y: number): [number, number] => [
      cx + x * Math.cos(tilt) - y * sq * Math.sin(tilt),
      cy + x * Math.sin(tilt) + y * sq * Math.cos(tilt),
    ];
    g.globalCompositeOperation = "lighter";
    for (let i = 0; i < 120; i++) {
      const rr = Math.abs(gauss()) * R * 0.45;
      const a = rand(0, Math.PI * 2);
      const [x, y] = P(Math.cos(a) * rr, Math.sin(a) * rr);
      blob(g, x, y, R * rand(0.06, 0.14), [150, 170, 255], 0.07);
    }
    for (const arm of [0, Math.PI])
      for (let i = 0; i < 1100; i++) {
        const t = rand(0.15, 1);
        const th = arm + t * 4.2;
        const rr = R * t;
        const [x, y] = P(Math.cos(th) * rr + gauss() * R * 0.035, Math.sin(th) * rr + gauss() * R * 0.035);
        if (Math.random() < 0.06) blob(g, x, y, R * 0.012, [255, 120, 170], 0.25);
        else {
          g.fillStyle = `rgba(190,210,255,${rand(0.3, 0.8)})`;
          g.fillRect(x, y, rand(0.8, 1.8), rand(0.8, 1.8));
        }
      }
    {
      const [x, y] = P(0, 0);
      blob(g, x, y, R * 0.28, [255, 220, 170], 0.18);
      blob(g, x, y, R * 0.1, [255, 240, 215], 0.55);
    }
    g.globalCompositeOperation = "destination-out";
    for (const arm of [0, Math.PI])
      for (let i = 0; i < 70; i++) {
        const t = rand(0.25, 0.95);
        const th = arm + t * 4.2 - 0.25;
        const [x, y] = P(Math.cos(th) * R * t, Math.sin(th) * R * t);
        blob(g, x, y, R * rand(0.015, 0.03), [0, 0, 0], 0.4);
      }
    g.globalCompositeOperation = "lighter";
    {
      const [x, y] = P(R * 0.35, R * 0.55);
      blob(g, x, y, R * 0.05, [255, 225, 190], 0.3);
    }
    nebula = c;
  }

  /** Named stars that belong to an object, in units of min(W, H) from its centre. */
  function featureStars(): [number, number, number, string][] {
    switch (sky.style) {
      case "orion":
        return [[-0.006, -0.004, 2.4], [0.006, -0.006, 2.2], [-0.002, 0.006, 2.6], [0.008, 0.004, 2]].map(
          ([x, y, r]) => [x!, y!, r!, "#e6f0ff"],
        );
      case "pleiades":
        return PLEIADES.map(([x, y], i) => [x * 0.2, y * 0.2, i === 0 ? 4.2 : rand(2.6, 3.6), "#d6e4ff"]);
      case "ring":
        return [[0, 0, 2, "#e8f0ff"]];
      default:
        return [];
    }
  }

  function buildNebula() {
    const custom = { veil: buildVeil, orion: buildOrion, pleiades: buildPleiades, ring: buildRing, globular: buildGlobular, galaxy: buildGalaxy };
    if (sky.style) return custom[sky.style]();
    const { c, g } = surface(0.5);
    g.globalCompositeOperation = "lighter";
    const cx = c.width * sky.cx;
    const cy = c.height * sky.cy;
    const R = Math.max(c.width, c.height);
    const h = sky.hues ?? [];
    const clouds: [number, number, number, Rgb, number][] = [
      [0.0, 0.0, 0.55, h[0]!, 0.22], [0.12, -0.1, 0.4, h[1]!, 0.2], [-0.18, 0.12, 0.38, h[2]!, 0.2],
      [0.25, 0.18, 0.3, h[3]!, 0.13], [-0.05, -0.22, 0.28, h[4]!, 0.12], [0.05, 0.05, 0.16, [255, 225, 205], 0.14],
      [-0.35, -0.05, 0.25, h[0]!, 0.12],
    ];
    for (const [dx, dy, r, [cr, cg, cb], al] of clouds) {
      for (let i = 0; i < 26; i++) {
        const x = cx + dx * R + gauss() * r * R * 0.35;
        const y = cy + dy * R + gauss() * r * R * 0.22;
        const rr = r * R * rand(0.25, 0.6);
        const grd = g.createRadialGradient(x, y, 0, x, y, rr);
        grd.addColorStop(0, `rgba(${cr},${cg},${cb},${al * (sky.gain ?? 1)})`);
        grd.addColorStop(1, "rgba(0,0,0,0)");
        g.fillStyle = grd;
        g.beginPath();
        g.arc(x, y, rr, 0, Math.PI * 2);
        g.fill();
      }
    }
    g.globalCompositeOperation = "destination-out";
    for (let i = 0; i < 14; i++) {
      const x = cx + gauss() * R * 0.18;
      const y = cy + gauss() * R * 0.1;
      const rr = R * rand(0.03, 0.08);
      const grd = g.createRadialGradient(x, y, 0, x, y, rr);
      grd.addColorStop(0, "rgba(0,0,0,0.22)");
      grd.addColorStop(1, "rgba(0,0,0,0)");
      g.fillStyle = grd;
      g.beginPath();
      g.arc(x, y, rr, 0, Math.PI * 2);
      g.fill();
    }
    nebula = c;
  }

  function buildStars() {
    stars = [];
    shooters = [];
    const area = W * H;
    const pal = sky.palette ?? PALETTE;
    const field = Math.min(1400, Math.floor(area / 1400));
    const cluster = Math.floor(Math.min(900, area / 2200) * (sky.cluster ?? 1));
    const ccx = W * sky.cx;
    const ccy = H * sky.cy;
    const spread = Math.min(W, H) * (sky.spread ?? 0.16);
    const ell = sky.round ? 1 : 1.4;
    const pow = sky.round ? 1.8 : 1.4;
    const make = (x: number, y: number, layer: number, bright: boolean, cols: readonly string[] = PALETTE, pinned = false): Star => ({
      x,
      y,
      layer,
      r: bright ? rand(1.1, 2.2) : rand(0.25, 1.05),
      col: cols[(Math.random() * cols.length) | 0]!,
      base: bright ? rand(0.75, 1) : rand(0.25, 0.85),
      tw: rand(0.6, 3.2),
      ph: rand(0, Math.PI * 2),
      spike: bright && Math.random() < 0.55,
      pinned,
    });
    for (let i = 0; i < field; i++) stars.push(make(rand(0, W), rand(0, H), Math.random(), Math.random() < 0.012));
    // Cluster stars are pinned: they move with their nebula instead of drifting off it.
    for (let i = 0; i < cluster; i++) {
      const d = Math.abs(gauss()) ** pow;
      const a = rand(0, Math.PI * 2);
      stars.push(make(ccx + Math.cos(a) * d * spread * ell, ccy + Math.sin(a) * d * spread, rand(0.3, 1), Math.random() < (sky.brightRate ?? 0.05), pal, true));
    }
    const m = Math.min(W, H);
    for (const [nx, ny, r, col] of featureStars())
      stars.push({ x: ccx + nx * m, y: ccy + ny * m, layer: 1, r, col, base: 1, tw: rand(0.4, 1), ph: rand(0, Math.PI * 2), spike: true, pinned: true });
  }

  function resize() {
    const DPR = Math.min(window.devicePixelRatio || 1, 2);
    W = canvas.clientWidth;
    H = canvas.clientHeight;
    if (!W || !H) return;
    canvas.width = W * DPR;
    canvas.height = H * DPR;
    ctx!.setTransform(DPR, 0, 0, DPR, 0, 0);
    buildNebula();
    buildStars();
  }

  function frame(now: number) {
    if (!W || !H) {
      if (!reduce) raf = requestAnimationFrame(frame);
      return;
    }
    const c = ctx!;
    const t = (now - t0) / 1000;
    tx += (mx - tx) * 0.04;
    ty += (my - ty) * 0.04;
    const cx = W * sky.cx;
    const cy = H * sky.cy;
    const bg = c.createRadialGradient(cx, cy, 0, cx, cy, Math.max(W, H) * 0.9);
    bg.addColorStop(0, "#0d0f24");
    bg.addColorStop(0.5, "#070816");
    bg.addColorStop(1, "#03040a");
    c.globalAlpha = 1;
    c.globalCompositeOperation = "source-over";
    c.fillStyle = bg;
    c.fillRect(0, 0, W, H);

    const breathe = 1 + Math.sin(t * 0.15) * 0.025;
    const ox = tx * -12 + Math.sin(t * 0.05) * 10;
    const oy = ty * -8 + Math.cos(t * 0.04) * 6;
    const nr = Math.sin(t * 0.02) * 0.04;
    const ncr = Math.cos(nr) * breathe;
    const nsr = Math.sin(nr) * breathe;
    c.save();
    c.translate(cx + ox, cy + oy);
    c.rotate(nr);
    c.scale(breathe, breathe);
    c.globalCompositeOperation = "lighter";
    c.globalAlpha = 0.95 + Math.sin(t * 0.3) * 0.05;
    c.drawImage(nebula, -cx, -cy, W, H);
    c.restore();

    c.globalCompositeOperation = "lighter";
    const rot = reduce ? 0 : t * 0.006;
    const cr = Math.cos(rot);
    const sr = Math.sin(rot);
    for (const s of stars) {
      const k = 0.25 + s.layer * 0.75;
      const dx = s.x - cx;
      const dy = s.y - cy;
      let x: number;
      let y: number;
      if (s.pinned) {
        x = cx + ox + dx * ncr - dy * nsr;
        y = cy + oy + dx * nsr + dy * ncr;
      } else {
        x = cx + dx * cr - dy * sr + tx * -30 * k;
        y = cy + dx * sr + dy * cr + ty * -20 * k;
        x = ((((x + (reduce ? 0 : t * 4 * k)) % (W + 40)) + W + 40) % (W + 40)) - 20;
      }
      if (y < -20 || y > H + 20) continue;
      const tw = reduce ? 1 : 0.55 + 0.45 * Math.sin(t * s.tw + s.ph) * Math.sin(t * s.tw * 0.37 + s.ph * 2);
      const a = Math.max(0.05, s.base * (0.6 + 0.4 * tw));
      c.globalAlpha = a;
      c.fillStyle = s.col;
      if (s.r > 1.05) {
        const hr = s.r * 6;
        const grd = c.createRadialGradient(x, y, 0, x, y, hr);
        grd.addColorStop(0, s.col);
        grd.addColorStop(0.15, s.col + "88");
        grd.addColorStop(1, "rgba(0,0,0,0)");
        c.globalAlpha = a * 0.5;
        c.fillStyle = grd;
        c.beginPath();
        c.arc(x, y, hr, 0, Math.PI * 2);
        c.fill();
        c.globalAlpha = a;
        c.fillStyle = "#fff";
        if (s.spike) {
          const len = s.r * (7 + 6 * tw);
          c.strokeStyle = s.col;
          c.globalAlpha = a * 0.55;
          c.lineWidth = 0.7;
          c.beginPath();
          c.moveTo(x - len, y);
          c.lineTo(x + len, y);
          c.moveTo(x, y - len);
          c.lineTo(x, y + len);
          c.stroke();
          c.globalAlpha = a;
        }
      }
      c.beginPath();
      c.arc(x, y, s.r, 0, Math.PI * 2);
      c.fill();
    }

    if (!reduce && shooters.length < 2 && Math.random() < 0.004) {
      const left = Math.random() < 0.5;
      shooters.push({ x: rand(W * 0.2, W), y: rand(0, H * 0.4), vx: left ? rand(6, 10) : -rand(6, 10), vy: rand(2.5, 4.5), life: 1 });
    }
    for (const sh of shooters) {
      sh.x += sh.vx;
      sh.y += sh.vy;
      sh.life -= 0.012;
      const grd = c.createLinearGradient(sh.x, sh.y, sh.x - sh.vx * 14, sh.y - sh.vy * 14);
      grd.addColorStop(0, "rgba(255,255,255,0.9)");
      grd.addColorStop(1, "rgba(255,255,255,0)");
      c.globalAlpha = Math.max(0, sh.life);
      c.strokeStyle = grd;
      c.lineWidth = 1.4;
      c.beginPath();
      c.moveTo(sh.x, sh.y);
      c.lineTo(sh.x - sh.vx * 14, sh.y - sh.vy * 14);
      c.stroke();
    }
    shooters = shooters.filter((s) => s.life > 0);
    c.globalAlpha = 1;
    c.globalCompositeOperation = "source-over";
    if (!reduce) raf = requestAnimationFrame(frame);
  }

  const onMove = (e: MouseEvent) => {
    mx = e.clientX / innerWidth - 0.5;
    my = e.clientY / innerHeight - 0.5;
  };
  const onResize = () => {
    resize();
    if (reduce) frame(performance.now());
  };
  addEventListener("mousemove", onMove);
  addEventListener("resize", onResize);
  resize();
  raf = requestAnimationFrame(frame);
  return () => {
    cancelAnimationFrame(raf);
    removeEventListener("mousemove", onMove);
    removeEventListener("resize", onResize);
  };
}
